import React, { useEffect, useRef, useState } from 'react';
import { Form, Field } from 'formik';
import MaskedInput from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { Checkbox, Info, ShowIf } from 'src/components';
import { InputErrorIcon } from 'src/assets/images';
import Modal from 'src/components/modal/modal';
import { ShapesDecorativeBorder } from 'src/components';
import FormikEnhanced from 'src/components/formik-enhanced/formik-enhanced';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import TealiumEvents from 'src/helpers/tealium-events';
import './monthly-turnover.scss';

const MINIMUN_TURNOVER = 0;
const MAXIMUM_TURNOVER = 999999999.99;
const PLACEHOLDER_VALUE = '0';
const turnoverMask = createNumberMask({
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ' ',
  allowDecimal: false,
  decimalSymbol: '.',
  decimalLimit: 0,
  integerLimit: 9,
  allowNegative: false,
  allowLeadingZeroes: false,
  inputMode: 'numeric',
});

export default function MonthlyTurnoverModal(props) {
  const ref = useRef(null);
  const modalRef = useRef(null);
  const [isSubmittingOverride, setIsSubmittingOverride] = useState(false);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      props.onHide();
    }
  };

  const toolTipText =
    'This is the amount you make monthly from card transactions ' +
    'before taking any expenses into account. If you do not know your turnover or ' +
    'are a new merchant and have just purchased your first device, please ' +
    'check the Checkbox for the next steps. ';

  const textTable = {
    dealer: {
      title: "Let's calculate the fees",
      subtitle: "What's the customer's estimated monthly card turnover?",
    },
    agent: {
      title: "Let's calculate the fees",
      subtitle: "What's the customer's estimated monthly card turnover?",
    },
    normal: {
      title:
        props.consumerType === CONSUMER_TYPE.owner
          ? "Let's calculate the fees"
          : "Let's calculate the customer's fees",
      subtitle:
        props.consumerType === CONSUMER_TYPE.owner
          ? "What's your estimated monthly card turnover?"
          : "What's the customer's estimated monthly card turnover?",
    },
    pos: {
      title: "Let's calculate your fees",
      subtitle: "What's your estimated monthly card turnover?",
    },
  };

  useEffect(() => {
    const loseFocusEventTypes = ['click'];
    loseFocusEventTypes.map((eventType) =>
      document.addEventListener(eventType, handleClickOutside, true),
    );
    return () =>
      loseFocusEventTypes.map((eventType) =>
        document.removeEventListener(eventType, handleClickOutside, true),
      );
  }, []);

  useEffect(() => {
    if (props.show) {
      ref.current.inputElement.focus();
    }
  }, [props.show]);

  const getFormattedTurnover = (turnover) => {
    if (turnover && typeof turnover !== 'string') {
      turnover = turnover.toString();
    }

    return turnover ? turnover.replace(/ /g, '') : turnover;
  };

  const isTurnoverValid = (values) => {
    const formattedTurnover = getFormattedTurnover(values.turnover);
    return (
      values.unknownTurnover ||
      (formattedTurnover &&
        formattedTurnover >= MINIMUN_TURNOVER &&
        formattedTurnover <= MAXIMUM_TURNOVER)
    );
  };
  function validateForm(values) {
    const errors = {};
    const invalidTurnover = !isTurnoverValid(values);
    if (invalidTurnover) {
      errors.turnover = `Please enter a valid monthly turnover`;
    }
    return errors;
  }

  async function submitForm(values, helpers) {
    values.turnover = getFormattedTurnover(values.turnover);
    const { setSubmitting } = helpers;
    setIsSubmittingOverride(true);
    props.setMonthlyCardTurnover(!values.unknownTurnover ? values.turnover : 0);
    await props.onNext(values, () => setSubmitting(false));
  }

  return (
    <FormikEnhanced
      initialValues={{
        turnover: props.monthlyCardTurnover() < 1 ? '' : props.monthlyCardTurnover(),
        unknownTurnover: false,
      }}
      validate={validateForm}
      onSubmit={submitForm}
      validateOnChange={true}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      {({ values, errors, touched, isSubmitting }) => {
        return (
          <Form className="has-overflow-auto">
            <Modal
              modalStyle="MonthlyTurnoverModal"
              modalWrapper={ShapesDecorativeBorder}
              modalWrapperProps={{ modalMode: true }}
              cardStyle="modal-card"
              modalProps={{ ref: modalRef }}
              headerStyle="modal-card-head"
              show={props.show}
              header={() => (
                <h5 className="heading-four has-text-centered has-margin-top-3 turnover-title">
                  {textTable[props.customerType].title}
                </h5>
              )}
              bodyStyle="modal-card-body-monthly-turnover"
              body={() => (
                <div className="body-container">
                  <div className="turnover-subtitle content-large-regular">
                    {textTable[props.customerType].subtitle}

                    <Info
                      className="turnover-tooltip"
                      text={toolTipText}
                      onOpenEvent={() => TealiumEvents.generalInfoClick(props.customerType)}
                    />
                  </div>
                  <div className="wrapper price-wrapper">
                    <p
                      className={
                        'currency-symbol symbol-turnover ' + (values.turnover ? '' : 'lighter')
                      }
                    >
                      R
                    </p>
                    <Field name="turnover">
                      {({ field }) => (
                        <MaskedInput
                          {...field}
                          ref={ref}
                          type="tel"
                          id="turnover"
                          mask={turnoverMask}
                          placeholder={PLACEHOLDER_VALUE}
                          className={`input modal-input`}
                          automationid="Onboarding_Field_Turnover_Modal"
                          disabled={props.isTurnoverReadOnly || values.unknownTurnover}
                          value={values.unknownTurnover ? PLACEHOLDER_VALUE : values.turnover}
                        />
                      )}
                    </Field>

                    <div
                      className={`grey-line ${errors.turnover && touched.turnover ? 'error' : ''}`}
                    />
                  </div>
                  <div className="modal-inline-error error-margin">
                    {errors.turnover && touched.turnover ? (
                      <div className="error-outer">
                        <img className="error-image" src={InputErrorIcon} />
                        <span>{errors.turnover}</span>
                      </div>
                    ) : (
                      <span>&nbsp;&nbsp;</span>
                    )}
                  </div>
                  <ShowIf condition={!props.isTurnoverReadOnly}>
                    <div className="unknown-turnover-checkbox-wrapper">
                      <Field
                        name="unknownTurnover"
                        type="checkbox"
                        component={MiniAppFocusWrapper}
                        innerComponent={Checkbox}
                        isLightMode={true}
                        checkboxText="I don't know my estimated card turnover"
                        checkboxTextStyle="unknown-turnover-text"
                        // disabled={isFieldLocked(LockedFieldNames.ACCOUNT_TRADING_AS)}
                        automationid="Onboarding_Create_Account_Monthly_Turnover_Field_Trading_Toggle"
                      />
                    </div>
                  </ShowIf>
                </div>
              )}
              footerStyle="modal-card-foot-monthly-turnover"
              footer={() => (
                <div className="center-items">
                  <button
                    type="submit"
                    className="button is-primary center-align"
                    automationid="Onboarding_Button_Monthly_Turnover_Calculate"
                    disabled={isSubmitting || !isTurnoverValid(values) || isSubmittingOverride}
                  >
                    <p>Continue</p>
                  </button>
                </div>
              )}
            />
          </Form>
        );
      }}
    </FormikEnhanced>
  );
}
